import {
  FREE_KITS,
  ITEM_SLOTS,
  type ItemDto,
  type ItemSlot,
  type ShopDto,
  kitById,
} from '@penalty-cup/shared';
import { Jersey } from '../components/Jersey.js';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../api.js';
import { payInvoice } from '../payments.js';
import { useApp } from '../store.js';
import { SHOP_ERRORS, SLOT_TITLES } from '../strings.js';
import { useBackButton } from '../telegram-back.js';

function Preview({ item }: { item: ItemDto }) {
  if (item.slot === 'kit') return <Jersey kit={item.id} className="shop__preview--jersey" />;
  const [a, b] = item.colors;
  return (
    <span
      className={`shop__preview shop__preview--${item.slot}`}
      style={{ ['--c1' as string]: a, ['--c2' as string]: b }}
      aria-hidden="true"
    />
  );
}

/** Магазин косметики: только внешний вид. За монеты — сразу, за Stars — счёт Telegram. */
export function Shop() {
  const token = useApp((s) => s.token);
  const me = useApp((s) => s.me);
  const setView = useApp((s) => s.setView);
  const applyShop = useApp((s) => s.applyShop);
  const reloadMe = useApp((s) => s.reloadMe);
  const back = useCallback(() => setView(null), [setView]);
  useBackButton(back);
  const [shop, setShop] = useState<ShopDto | null>(null);
  const [slot, setSlot] = useState<ItemSlot>('ball');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.shop(token).then(
      (s) => {
        if (!alive) return;
        setShop(s);
        applyShop(s);
      },
      () => alive && setError('Магазин не загрузился. Проверьте связь'),
    );
    return () => {
      alive = false;
    };
  }, [token, applyShop]);

  if (!shop || !me) {
    return (
      <main className="screen shop">
        <h1 className="screen__title">Магазин</h1>
        <p className="screen__lead">{error ?? 'Загружаем…'}</p>
      </main>
    );
  }

  const act = async (id: string, fn: () => Promise<ShopDto | null | void>) => {
    if (!token || busy) return;
    setBusy(id);
    setError(null);
    try {
      const s = await fn();
      if (s) {
        setShop(s);
        applyShop(s);
      }
    } catch (err) {
      setError(
        SHOP_ERRORS[err instanceof ApiError ? err.message : ''] ?? 'Не получилось. Попробуйте ещё раз',
      );
    } finally {
      setBusy(null);
    }
  };

  const owned = (item: ItemDto) => item.free || shop.owned.includes(item.id);
  const isOn = (item: ItemDto) =>
    item.slot === 'kit' ? kitById(me.kit).id === item.id : shop.equipped[item.slot] === item.id;

  const buyStars = (item: ItemDto) =>
    act(item.id, async () => {
      const { link } = await api.starsInvoice(token!, item.id);
      if (!(await payInvoice(link))) return;
      // Предмет выдаётся по уведомлению Telegram — подождём его немного
      for (let i = 0; i < 10; i++) {
        const s = await api.shop(token!);
        if (s.owned.includes(item.id)) return s;
        await new Promise((r) => setTimeout(r, 1000));
      }
      setError('Оплата прошла, предмет появится через минуту');
    });

  // Во вкладке «Форма» — и обычные формы (бесплатно), чтобы переодеться обратно
  const freeKits: ItemDto[] = FREE_KITS.map((k) => ({
    id: k.id,
    slot: 'kit',
    name: k.name,
    priceCoins: null,
    priceStars: null,
    free: true,
    colors: [k.color, k.accent],
  }));
  const items = [...shop.items.filter((i) => i.slot === slot), ...(slot === 'kit' ? freeKits : [])];
  return (
    <main className="screen shop">
      <h1 className="screen__title">Магазин</h1>
      <p className="screen__lead">🪙 {shop.coins} · только внешний вид, на игру не влияет</p>
      <div className="shop__tabs" role="tablist">
        {ITEM_SLOTS.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={s === slot}
            className={`chip ${s === slot ? 'chip--on' : ''}`}
            onClick={() => setSlot(s)}
          >
            {SLOT_TITLES[s]}
          </button>
        ))}
      </div>
      {error && (
        <p className="field__hint field__hint--error" role="alert">
          {error}
        </p>
      )}
      <ul className="shop__list">
        {items.map((item) => (
          <li key={item.id} className={`shop__item ${isOn(item) ? 'shop__item--on' : ''}`}>
            <Preview item={item} />
            <span className="shop__name">{item.name}</span>
            {owned(item) ? (
              <button
                type="button"
                className="chip"
                disabled={isOn(item) || !!busy}
                onClick={() =>
                  void act(item.id, () =>
                    api.equipItem(token!, item.slot, item.id).then(async (s) => {
                      if (item.slot === 'kit') await reloadMe();
                      return s;
                    }),
                  )
                }
              >
                {isOn(item) ? 'Надето' : 'Надеть'}
              </button>
            ) : item.priceCoins !== null ? (
              <button
                type="button"
                className="chip"
                disabled={shop.coins < item.priceCoins || !!busy}
                onClick={() => void act(item.id, () => api.buyItem(token!, item.id))}
              >
                {busy === item.id ? '…' : `${item.priceCoins} 🪙`}
              </button>
            ) : item.priceStars !== null && shop.starsEnabled ? (
              <button type="button" className="chip" disabled={!!busy} onClick={() => void buyStars(item)}>
                {busy === item.id ? '…' : `${item.priceStars} ⭐`}
              </button>
            ) : (
              <span className="shop__soon">скоро</span>
            )}
          </li>
        ))}
      </ul>
      <p className="field__hint">Монеты — за матчи и турниры. Купить их за деньги нельзя.</p>
    </main>
  );
}
